import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";

import { DeliveryLocationsByCustomer, DeliveryLocationsResponse, LocationCustomer, SelectedCustomer } from "./types";

// Backs the "Choose Locations Accessible From {hostname}" section. Mirrors
// hostnameConfigController.js's getAllDeliveryLocations()/saveHostnameToLocations():
// fetched once per customer session (not per selected hostname), independent of
// useHostnameConfigs. `enabled` should be isBbotAdmin - this section is invisible to
// every other user, so there's no reason to fetch a potentially large connected-
// customer dataset (self + chain + vendees) for the common case that never renders it.
export const useHostnameLocations = (selectedCustomer: SelectedCustomer, enabled: boolean) => {
  const customerId = selectedCustomer?.customer_id;

  const [locationsByCustomer, setLocationsByCustomer] = useState<DeliveryLocationsByCustomer>({});
  const [customers, setCustomers] = useState<LocationCustomer[]>([]);
  const [fulfillmentMethods, setFulfillmentMethods] = useState<string[]>([]);
  const [prettyNames, setPrettyNames] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(enabled);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  // Tracks the customer a load() call was actually issued for, so a slow response
  // arriving after the admin has since switched to a different customer can be
  // detected and dropped instead of overwriting that newer customer's own state.
  const customerIdRef = useRef(customerId);
  customerIdRef.current = customerId;

  const load = useCallback(async (): Promise<boolean> => {
    const requestedCustomerId = customerId;
    if (!requestedCustomerId || !enabled) return false;
    setIsLoading(true);
    try {
      const res = await axios.post<DeliveryLocationsResponse>("/owner/getAllDeliveryLocationsForConnectedCustomers", {
        customerId: requestedCustomerId,
      });
      if (customerIdRef.current !== requestedCustomerId) return false;
      setLocationsByCustomer(res.data.delivery_locations);
      setCustomers(res.data.customers);
      setFulfillmentMethods(res.data.fulfillment_methods);
      setPrettyNames(res.data.pretty_names);
      return true;
    } catch (error) {
      if (customerIdRef.current === requestedCustomerId) {
        generalErrorAlert(error, "Trouble retrieving delivery locations. Please refresh and try again.");
      }
      return false;
    } finally {
      if (customerIdRef.current === requestedCustomerId) setIsLoading(false);
    }
  }, [customerId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  // Mirrors saveHostnameToLocations(): sends the whole per-customer structure, not a
  // diff. The legacy comment on this guard specifically calls it deadlock-prevention,
  // not UX polish - a ref (synchronous) is used instead of state so a second click
  // fired before the first render commits still can't slip through.
  const saveHostnameToLocations = useCallback(
    async (hostname: string, deliveryLocations: DeliveryLocationsByCustomer) => {
      if (isSavingRef.current) return false;
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await axios.post("/owner/addHostnameToDeliveryLocations", { hostname, deliveryLocations });
        notification.success({ message: "Successfully saved location codes" });
        // A refresh failure here must not be reported as an overall success, or the
        // caller would switch to a "saved" display view still showing stale pre-save
        // data - load() already raises its own error toast when this happens.
        return await load();
      } catch (error) {
        generalErrorAlert(error, "Trouble updating hostname. Please refresh and try again.");
        return false;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [load]
  );

  return {
    locationsByCustomer,
    locationCustomers: customers,
    fulfillmentMethods,
    prettyNames,
    isLoadingLocations: isLoading,
    isSavingLocations: isSaving,
    saveHostnameToLocations,
  };
};
